import { getIdToken } from '@/services/auth';
import type { paths } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

export type ReconcileProviderRequestBody =
  paths['/v2/auth/reconcile-provider']['post']['requestBody']['content']['application/json'];

export type ReconcileProviderResponse =
  paths['/v2/auth/reconcile-provider']['post']['responses']['200']['content']['application/json'];

/**
 * Detection-only endpoint for account linking status (PreSignUp Lambda handles all automatic linking).
 *
 * Called in OAuth callback after Google sign-in. Detects if Google identity is already linked to
 * native account. Native account remains primary with custom:user_id; Google added to identities[]
 * array with primary: false.
 *
 * Response pattern:
 * - For linked cases: { linkStatus: 'new' | 'linked' }
 *   - linkStatus: 'new' = just linked (not yet acknowledged)
 *   - linkStatus: 'linked' = already linked (acknowledged or existed before)
 * - For non-linked cases: { outcome: 'npi_unverified' | 'federated_only' | ... }
 *   - outcome indicates the reason (new user needs NPI, OAuth-only complete, etc.)
 *
 * Idempotent and safe to call multiple times. Use linkStatus === 'new' to determine if Account linked
 * analytics event should fire for newly linked accounts.
 */
export async function reconcileProvider(): Promise<ReconcileProviderResponse | null> {
  const idToken = await getIdToken();

  if (!idToken) {
    return null;
  }

  const { data, error } = await fetchClient.POST('/v2/auth/reconcile-provider', {
    body: {} as ReconcileProviderRequestBody,
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (error) {
    throw error;
  }

  return (data as unknown as ReconcileProviderResponse) || null;
}
